import axios from 'axios';
import { Helpers } from '../helpers/Helpers';

/**
 * Vardiya (İnsan Kaynakları Faz 2, 03.11.2026): tanımlar, dönen planlar, personel atamaları ve günlük değişiklik.
 * Vardiyalı personelde günün normal süresi, hatırlatma saati ve eksik çıkış sınırı vardiyadan gelir.
 */

export interface VardiyaMolasi {
    ad: string | null
    /** "HH:mm" */
    baslangic: string
    bitis: string
}

export interface VardiyaTanimiItem {
    vardiyaTanimiId: string
    kod: string
    ad: string
    /** "HH:mm"; bitiş başlangıçtan önceyse ertesi gün (gece vardiyası). */
    baslangic: string
    bitis: string
    araBaslangic: string | null
    araBitis: string | null
    /** Ara ve molalar (adlı, Çalışma Saatleri'ndeki gibi). */
    molalar: VardiyaMolasi[]
    bildirimDk: number | null
    hatirlatmaDk: number | null
    aktif: boolean
    /** Ara ve molalar düşülmüş çalışma süresi (dk). */
    calismaDk: number
    geceYarisiniGecer: boolean
    geceVardiyasi: boolean
    uyari: string | null
    kullanimSayisi: number
    isSelected?: boolean
}

export interface VardiyaTanimiKaydetIstegi {
    vardiyaTanimiId?: string | null
    kod: string
    ad: string
    baslangic: string
    bitis: string
    araBaslangic: string | null
    araBitis: string | null
    /** Ara ve molalar; en fazla 8. Ara da buradan gönderilir (araBaslangic / araBitis boş). */
    molalar: VardiyaMolasi[]
    bildirimDk: number | null
    hatirlatmaDk: number | null
    aktif: boolean
}

export interface VardiyaPlaniItem {
    vardiyaPlaniId: string
    ad: string
    /** "S,S,AK,AK,G,G,HT,HT" */
    desen: string
    gunSayisi: number
    aktif: boolean
    atamaSayisi: number
    uyarilar: string[]
    isSelected?: boolean
}

export interface VardiyaPlaniKaydetIstegi {
    vardiyaPlaniId?: string | null
    ad: string
    desen: string
    aktif: boolean
}

export interface VardiyaAtamasiItem {
    vardiyaAtamasiId: string
    personnelId: string
    personelAdi: string | null
    vardiyaPlaniId: string
    planAdi: string
    /** "yyyy-MM-dd" */
    baslangic: string
    bitis: string | null
    baslangicStr: string
    bitisStr: string | null
    giren: string | null
    tarih: string
    isSelected?: boolean
}

export interface VardiyaAtamasiKaydetIstegi {
    vardiyaAtamasiId?: string | null
    personnelIdler: string[]
    vardiyaPlaniId: string
    baslangic: string
    bitis: string | null
}

export interface VardiyaCizelgeGunu {
    tarih: string
    gun: number
    haftaGunu: string
    ozelGun: string | null
}

export interface VardiyaCizelgeHucresi {
    tarih: string
    /** Vardiya kodu, "HT" hafta tatili, null plan yok. */
    kod: string | null
    vardiyaTanimiId: string | null
    saat: string | null
    tatil: boolean
    degisiklik: boolean
    aciklama: string | null
    izinKodu: string | null
    izinAdi: string | null
    uyari: string | null
}

export interface VardiyaCizelgePersoneli {
    personnelId: string
    personelAdi: string | null
    planAdi: string | null
    gunler: VardiyaCizelgeHucresi[]
    ozet: { [kod: string]: number }
}

export interface VardiyaCizelgesiYaniti {
    gunler: VardiyaCizelgeGunu[]
    personeller: VardiyaCizelgePersoneli[]
    vardiyalar: VardiyaTanimiItem[]
    uyarilar: string[]
}

export interface VardiyaGunDegistirIstegi {
    personnelId: string
    tarih: string
    vardiyaTanimiId: string | null
    tatil: boolean
    planaDon: boolean
    aciklama: string | null
}

/** Gün değişimi isteği (İK Faz 3). */
export interface TakasItem {
    takasId: string
    personnelId: string
    personelAdi: string | null
    tarih: string
    tarihStr: string
    mevcutKod: string | null
    mevcutSaat: string | null
    istenenKod: string
    istenenSaat: string | null
    karsiPersonnelId: string | null
    karsiPersonelAdi: string | null
    karsiMevcutKod: string | null
    aciklama: string | null
    durum: number
    durumAdi: string
    kararVeren: string | null
    kararZamani: string | null
    redNedeni: string | null
    olusturmaZamani: string
    kararVerebilir: boolean
}

interface Yanit<T> { isSuccess: boolean, message?: string, data: T }

const cagir = async <T>(uc: string, govde: any = {}): Promise<Yanit<T>> => {
    try {
        console.log('SERVICE CALL - ' + uc, JSON.stringify(govde));
        const resp = await axios.post(Helpers.getEndpoint() + '/' + uc, govde);
        return Helpers.dataParser(resp);
    } catch (error) {
        throw Helpers.parseErrorMessage(error);
    }
}

export class VardiyaService {
    static tanimListesi = () => cagir<VardiyaTanimiItem[]>('VardiyaTanimi/GetList');
    static tanimKaydet = (istek: VardiyaTanimiKaydetIstegi) => cagir<VardiyaTanimiItem>('VardiyaTanimi/Kaydet', istek);
    static tanimSil = (ids: string[]) => cagir<boolean>('VardiyaTanimi/Sil', { ids });
    static planListesi = () => cagir<VardiyaPlaniItem[]>('VardiyaPlani/GetList');
    static planKaydet = (istek: VardiyaPlaniKaydetIstegi) => cagir<VardiyaPlaniItem>('VardiyaPlani/Kaydet', istek);
    static planSil = (ids: string[]) => cagir<boolean>('VardiyaPlani/Sil', { ids });
    static cizelge = (baslangic: string, bitis: string) => cagir<VardiyaCizelgesiYaniti>('VardiyaCizelgesi/GetList', { baslangic, bitis });
    static gunDegistir = (istek: VardiyaGunDegistirIstegi) => cagir<VardiyaCizelgeHucresi>('VardiyaCizelgesi/GunDegistir', istek);
    static atamaListesi = () => cagir<VardiyaAtamasiItem[]>('VardiyaCizelgesi/AtamaListesi');
    static atamaKaydet = (istek: VardiyaAtamasiKaydetIstegi) => cagir<VardiyaAtamasiItem[]>('VardiyaCizelgesi/AtamaKaydet', istek);
    static atamaSil = (ids: string[]) => cagir<boolean>('VardiyaCizelgesi/AtamaSil', { ids });
    static takasListesi = () => cagir<TakasItem[]>('VardiyaCizelgesi/TakasListesi', {});
    static takasKarar = (id: string, onay: boolean, redNedeni: string | null) => cagir<TakasItem>('VardiyaCizelgesi/TakasKarar', { id, onay, redNedeni });
}
